import { useEffect, useId, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'

import { DialogSurface } from './DialogSurface.tsx'
import { ModalFrameContext, type ModalFrame } from './modalFrame.ts'
import './overlay.css'

/**
 * 모달 — 포커스/닫힘은 여기, 외관은 DialogSurface가 담당한다.
 *
 * ⚠ 쓰는 곳: 확약 · 계정 대화 상자, 그리고 「추가」 창 넷(작업 · 시료 · 레시피 · 랩웨어 — eric 2026-09-30
 *   「각각을 눌렀을 때 나오는 창이 팝업창이 뜨도록」). 그 외에는 함부로 만들지 마라 —
 *   목록 상세는 **오른쪽 서랍**, 설정 변경은 **그 자리 드롭다운**이다(§1-3 · §14-2).
 * 페이지 한 장짜리 폼(`DetailPage`)은 창 안에 그대로 넣으면 창 모양으로 선다(`modalFrame.ts`).
 * ⚠ **E-STOP 에는 절대 붙이지 마라.** 확인 대화상자 없이 즉시 적용이다(§1-3).
 *
 * 와이어프레임에는 `role="dialog"` 도 포커스 트랩도 없다(§11-4-4). 여기서 채운다.
 */
export type ModalProps = {
  /** 제목(한글) */
  title: string
  /**
   * 제목 앞 표지(예: 경고 ▲). 🔴 **장식이다** — 호출부가 `aria-hidden` 으로 넘긴다. 접근 이름은 `title` 글자만이다.
   * 지금은 작업 중지 확인 창 하나만 쓴다(eric 2026-10-26).
   */
  titleMark?: ReactNode | undefined
  /** 제목 오른쪽 같은 줄 보조 표기 — `DialogSurface` 참조 */
  titleAside?: ReactNode | undefined
  children: ReactNode
  onClose: () => void
  /** 바닥 버튼 줄 */
  footer?: ReactNode | undefined
  /**
   * 콘솔 폭: sm(기본 확인창 560) · md(일반 폼 960) · lg(표/두 칸 1280).
   * default/wide는 기존 화면의 단계적 이행용이다. 새 호출은 sm/md/lg를 쓴다.
   */
  size?: 'sm' | 'md' | 'lg' | 'default' | 'wide' | undefined
  /**
   * 덮개가 가리는 범위. 🔴 **`body` 는 머리줄 · 상시 작업 줄을 덮지 않는다** — E-STOP 은 창이 떠 있어도
   * 눌려야 한다(07 §6). 셸 본문(`.shell__body`) 안에서 절대 배치로 그 칸만 덮는다.
   * 기본(`viewport`)은 화면 전체다(사용자 추가 · 자가 진단 · IP 편집 — 짧은 창이라 종전 그대로).
   */
  scope?: 'viewport' | 'body' | undefined
  /**
   * 저장 중 — × · Esc · 바깥 누름으로 **닫히지 않는다**(요청이 나간 채 창이 사라지면 결과를 못 본다).
   * 안에 든 화면이 `useModalBusy` 로 알려도 같다. 둘 중 하나라도 참이면 막힌다.
   */
  busy?: boolean | undefined
  /**
   * 바깥(덮개)을 누르면 닫힌다. 기본은 꺼짐 — 확약 · 계정 대화 상자는 종전 그대로다.
   * 누름이 덮개에서 «시작하고» 덮개에서 끝나야 닫힌다(안에서 글자를 긁다 밖에서 놓으면 안 닫힌다).
   */
  closeOnBackdrop?: boolean | undefined
  /**
   * 열릴 때 포커스를 둘 곳. `field` 면 첫 입력칸(없으면 창). 기본은 창 자체(종전 그대로).
   */
  initialFocus?: 'dialog' | 'field' | undefined
  /**
   * `form` — 추가 창 내부 폼 레이아웃(eric 2026-09-30 「포맷을 모두 동일하게」). 넉넉한 안쪽 여백 · 머리/바닥 고정 ·
   * 본문만 스크롤 · 라벨이 입력 위에 서는 폼 격자. 🔴 **직접 넘기지 말고 `AddModal` 을 쓴다**(옵션 조합이 한 곳이다).
   * `stop-confirm` — 작업 중지 전용의 좁은 팝업. 스타일은 batchStrip.css 에서만 정의한다.
   * 콘솔에서는 모든 variant가 공용 DialogSurface 외관을 쓴다.
   */
  variant?: 'default' | 'form' | 'stop-confirm' | undefined
  /** 제목 밑 한 줄 옅은 설명. 있을 때만 선다 */
  description?: string | undefined
}

const FIELD =
  'input:not([type="hidden"]):not([disabled]):not([readonly]):not([aria-disabled="true"]), select:not([disabled]), textarea:not([disabled]):not([readonly])'

const FOCUSABLE =
  'button:not([aria-disabled="true"]), [href], input:not([readonly]), select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * 떠 있는 창 — 연 순서대로. 🔴 Esc · Tab 은 **맨 위 창만** 받는다.
 * 창이 겹치면 둘 다 문서에 `keydown` 을 걸어 두는데, 먼저 불린 쪽이 닫고 나머지도 닫아 Esc 한 번에 둘 다 닫힌다.
 */
const openStack: HTMLElement[] = []

/**
 * 맨 위 창인가. ⚠ 연 순서만으로는 못 가린다 — 창 **안에** 그려진 확인 대화는 React 가 자식 effect 를
 * 먼저 돌려 바깥 창보다 먼저 줄에 선다. 그래서 다른 창을 품은 창은 맨 위가 아니고, 남은 것 중 나중에 연 것이 맨 위다.
 */
function isTopModal(dialog: HTMLElement): boolean {
  const leaves = openStack.filter((d) => !openStack.some((o) => o !== d && d.contains(o)))
  return leaves[leaves.length - 1] === dialog
}

export function Modal({
  title,
  titleMark,
  titleAside,
  children,
  onClose,
  footer,
  size = 'default',
  scope = 'viewport',
  busy = false,
  closeOnBackdrop = false,
  initialFocus = 'dialog',
  variant = 'default',
  description,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  /** 안에 든 화면이 `useModalBusy` 로 알린 저장 중 */
  const [innerBusy, setInnerBusy] = useState(false)
  /** 바닥 줄 자리 — 안에 든 폼이 버튼을 여기로 옮긴다(`modalFrame.ts`) */
  const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null)
  const blocked = busy || innerBusy
  const blockedRef = useRef(blocked)
  useEffect(() => {
    blockedRef.current = blocked
  })
  /** 덮개에서 누름이 시작됐는가 — 안에서 긁다가 밖에서 놓은 것은 닫지 않는다 */
  const pressedOnBackdrop = useRef(false)

  const frame = useMemo<ModalFrame>(
    () => ({ footer: footerSlot, setBusy: setInnerBusy }),
    [footerSlot],
  )

  /**
   * 🔴 **`onClose` 를 의존성으로 두지 않는다.** 호출부가 `onClose={() => setOpen(false)}` 로
   *    인라인 함수를 주는 것이 정상인데, 그러면 렌더마다 새 함수라 아래 effect 가 매번 다시 돌고
   *    **`dialog.focus()` 가 입력 포커스를 빼앗는다.** 실제로 사용자 추가 모달은 이 때문에
   *    **글자 하나만 입력되고 끊겼다**(2026-09-18 재현·수정). 최신 콜백은 ref 로 읽는다.
   */
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })
  /** 열릴 때 한 번만 읽는다 */
  const initialFocusRef = useRef(initialFocus)

  /** × · 바깥 누름. 🔴 저장 중(`busy`)에는 닫지 않는다 */
  const requestClose = () => {
    if (blockedRef.current) return
    onCloseRef.current()
  }

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog === null) return undefined

    const previouslyFocused = document.activeElement
    const field =
      initialFocusRef.current === 'field' ? dialog.querySelector<HTMLElement>(FIELD) : null
    ;(field ?? dialog).focus()

    openStack.push(dialog)

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isTopModal(dialog)) return
      if (e.key === 'Escape') {
        // 🔴 하위 위젯(콤보박스 · 메뉴 · 칩 입력)이 자기 목록을 닫느라 이미 쓴 Esc 는 창을 닫지 않는다.
        //    안 보면 목록만 닫으려던 Esc 에 창까지 닫혀 적어 둔 것이 통째로 사라진다(시료 추가 창 실제 사고).
        //    한글 조합 중의 Esc 는 조합 취소다.
        if (e.defaultPrevented || e.isComposing) return
        e.preventDefault()
        // 🔴 저장 중에는 닫지 않는다(`busy`)
        if (!blockedRef.current) onCloseRef.current()
        return
      }
      if (e.key !== 'Tab') return

      // 포커스 트랩 — 모달 밖으로 탭이 빠져나가지 않는다
      const targets = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (targets.length === 0) {
        e.preventDefault()
        return
      }
      const first = targets[0]
      const last = targets[targets.length - 1]
      if (first === undefined || last === undefined) return

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const at = openStack.lastIndexOf(dialog)
      if (at >= 0) openStack.splice(at, 1)
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    }
    // 열릴 때 한 번만. 의존성에 `onClose` 를 넣으면 포커스를 다시 뺏는다(위 주석)
  }, [])

  const sizeClass =
    size === 'wide'
      ? ' a-modal--wide'
      : size === 'lg'
        ? ' a-modal--wide a-modal--lg a-modal--size-lg'
        : size === 'md'
          ? ' a-modal--size-md'
          : size === 'sm'
            ? ' a-modal--size-sm'
            : ''
  const variantClass = variant === 'default' ? '' : ` a-modal--${variant}`
  const descId = useId()

  return (
    <div
      className={scope === 'body' ? 'a-modalbg a-modalbg--body' : 'a-modalbg'}
      {...(closeOnBackdrop
        ? {
            onMouseDown: (e: MouseEvent) => {
              pressedOnBackdrop.current = e.target === e.currentTarget
            },
            onMouseUp: (e: MouseEvent) => {
              const onBackdrop = pressedOnBackdrop.current && e.target === e.currentTarget
              pressedOnBackdrop.current = false
              if (onBackdrop) requestClose()
            },
          }
        : {})}
    >
      <DialogSurface
        aria-busy={blocked ? true : undefined}
        aria-describedby={description === undefined ? undefined : descId}
        aria-labelledby={titleId}
        aria-modal="true"
        busy={blocked}
        className={`a-modal${sizeClass}${variantClass}`}
        description={description}
        descriptionId={descId}
        footer={footer}
        footerRef={footer === undefined ? setFooterSlot : undefined}
        onClose={requestClose}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        title={title}
        titleId={titleId}
        titleAside={titleAside}
        titleMark={titleMark}
      >
        <ModalFrameContext.Provider value={frame}>{children}</ModalFrameContext.Provider>
      </DialogSurface>
    </div>
  )
}
